/**
 * The setup links /portal handover prints (docs/integration-portal-claude-design-proposal.md, PD-7, in the SVN repo):
 * - Settings › Portals: `#/settings/portals?add=1&ws=<workspace>&name=…&icon=…&url=…&visibleTo=everyone|admins&web=1&app=1`
 *   opens Add portal filled in (or the portal already at that address, to edit);
 * - Studio: `#/studio?id=<module>&t=activity|standardActivity&tid=<activity>&openWith=<the portal's address>` sets that
 *   activity's Open with to the portal.
 * A person checks and saves both: nothing is saved from a link. The links carry names and addresses only.
 */
import { PORTAL_ICONS, DEFAULT_PORTAL_ICON } from "./portalNav";
import { PORTAL_NAME_MAX } from "./portalFormat";

function one(value) {
  return Array.isArray(value) ? value[0] : value;
}

function text(value) {
  const v = one(value);
  return v === undefined || v === null ? "" : String(v).trim();
}

/** "1", "true", "yes" and "on" are on; "0", "false", "no" and "off" are off; anything else is the fallback. */
function flag(value, fallback) {
  const v = text(value).toLowerCase();
  if (["1", "true", "yes", "on"].includes(v)) return true;
  if (["0", "false", "no", "off"].includes(v)) return false;
  return fallback;
}

/** The address as the server keeps it: no trailing spaces; scheme and host compared without case. */
function sameAddress(a, b) {
  const left = text(a), right = text(b);
  if (!left || !right) return false;
  try {
    const x = new URL(left), y = new URL(right);
    return x.origin.toLowerCase() === y.origin.toLowerCase() && x.pathname === y.pathname && x.search === y.search;
  } catch (e) {
    return left === right;
  }
}

/**
 * Settings › Portals: what an `add=1` link asks for, as the dialog's form, or null when the address asks for nothing.
 * An icon outside the dialog's list falls back to the default; the name is cut to the dialog's limit.
 */
export function addLinkOf(query) {
  const q = query || {};
  if (!flag(q.add, false)) return null;
  const icon = text(q.icon);
  const visibleTo = text(q.visibleTo || q.visibility).toLowerCase();
  return {
    workspaceId: text(q.ws),
    form: {
      icon: PORTAL_ICONS.some((x) => x.name === icon) ? icon : DEFAULT_PORTAL_ICON,
      name: text(q.name).slice(0, PORTAL_NAME_MAX),
      url: text(q.url),
      visibility: visibleTo === "admins" || visibleTo === "workspace admins" ? "admins" : "everyone",
      showOnWeb: flag(q.web, true),
      showOnApp: flag(q.app, true),
    },
  };
}

/** Studio: the portal address an Open with link names for this activity, or "" when the link is for another one. */
export function openWithOf(query, activityId) {
  const q = query || {};
  const address = text(q.openWith);
  if (!address || !["activity", "standardActivity"].includes(text(q.t))) return "";
  return activityId !== undefined && activityId !== null && text(q.tid) === String(activityId) ? address : "";
}

/** The workspace's portal at that address, or null. */
export function portalAt(portals, address) {
  return (Array.isArray(portals) ? portals : []).find((p) => p && p.id && sameAddress(p.url, address)) || null;
}

export default { addLinkOf, openWithOf, portalAt };
